/**
 * MediaStudioAISuggestionBlock — AI-Temp surface that surfaces 3-4 prompt
 * starters for the active media kind. Tapping a chip fills the textarea
 * below (does NOT instant-fire) so the user can edit before sending.
 *
 * Dismissable: when the user clicks the X, the parent hides the block for
 * the rest of this dialog session (a simple parent-side `dismissed` flag —
 * not persisted across sessions; the block re-appears next dialog open).
 */
import { SparkleIcon } from '@phosphor-icons/react';
import { useState } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { QUIET_ACTION } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

/**
 * Canonical chip className for skill-purple AI-suggestion chips used by
 * contained prompt-starter examples. The production rail's "Suggest" trigger
 * is a quiet text action; it opens these starters in a menu instead of
 * rendering them as a second row of pills.
 *
 * Single source of truth shared by:
 *   - Style-guide prompt-starter examples
 *   - PostInspectorShowcase + UnifiedMediaStudioShowcase + MediaStudioShowcase
 *
 * Used to read like 5 hand-rolled copies of the same className. If you find
 * yourself writing `border-skill/30 bg-skill/[0.04] ...` inline, import this.
 */
export const SKILL_CHIP_CLASS = cn(
  // h-8 fixed at every breakpoint — this chip sits in the SAME scrolling row
  // as ACTION_PILL_CLASS pills (Create a post / Make video / Add to project).
  'h-8 rounded-full border border-skill/30 bg-skill/[0.04] px-3 text-detail font-medium',
  'text-skill/90 hover:bg-skill/10 hover:border-skill/50',
  'transition-colors',
);

export interface AISuggestion {
  /** Stable id for telemetry / keyed render. */
  id: string;
  /** Visible chip label. Imperative phrasing — "Change the framing", not "Framing". */
  label: string;
  /**
   * Text dropped into the composer textarea on click. The user can edit
   * before sending, so this CAN be a complete sentence ("Change the framing
   * to a tighter 4:5 portrait crop") or a starter ("Change the lighting to
   * ").
   */
  prefill: string;
}

export interface MediaStudioAISuggestionBlockProps {
  suggestions: AISuggestion[];
  /** Called when the user taps a chip — parent fills the composer. */
  onPickSuggestion: (suggestion: AISuggestion) => void;
  /** Called when the user dismisses the block. */
  onDismiss?: () => void;
}

export function MediaStudioAISuggestionBlock({
  suggestions,
  onPickSuggestion,
}: MediaStudioAISuggestionBlockProps) {
  const [open, setOpen] = useState(false);

  if (suggestions.length === 0) return null;

  // Progressive disclosure (2026-06-27): the editing starters used to render as
  // an always-on dashed chip cluster, which competed with the composer + action
  // chips for attention. Collapsed behind one compact "✨ Ideas" affordance —
  // the SAME pattern as the post inspector's caption ✨ — so the two dialogs
  // read as siblings and the starters appear only when the user wants them.
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            QUIET_ACTION,
            'h-8 shrink-0 px-1.5 text-caption font-medium text-skill/80 hover:text-skill',
          )}
        >
          <SparkleIcon className="size-3.5" weight="duotone" />
          Suggest
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-60 p-1.5">
        <p className="px-2 pb-1 pt-0.5 text-detail font-medium text-muted-foreground/70">
          Ask Mora to…
        </p>
        <div className="flex flex-col">
          {suggestions.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => {
                onPickSuggestion(s);
                setOpen(false);
              }}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-skill/10 hover:text-skill"
            >
              {s.label}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// 'Try a different color' and 'Try lifestyle staging' were removed from this
// list: both duplicated a NEXT_STEPS-derived chip in SuggestionChips.tsx's
// bottom action row under the SAME label but with DIFFERENT behavior (this
// block's chips prefill the composer; the registry chips auto-send/route to
// a named action). One label must never mean two things — see
// `next-steps.ts` header comment.
export const IMAGE_AI_SUGGESTIONS: AISuggestion[] = [
  {
    id: 'change-background',
    label: 'Change the background',
    prefill: 'Change the background to ',
  },
  {
    id: 'adjust-lighting',
    label: 'Adjust the lighting',
    prefill: 'Adjust the lighting to ',
  },
];

export const VIDEO_AI_SUGGESTIONS: AISuggestion[] = [
  {
    id: 'change-framing',
    label: 'Change the framing',
    prefill: 'Change the framing to ',
  },
  {
    id: 'adjust-motion',
    label: 'Adjust motion',
    prefill: 'Adjust the camera motion to ',
  },
  {
    id: 'swap-mood',
    label: 'Swap the mood',
    prefill: 'Make the mood ',
  },
  {
    id: 'extend-shot',
    label: 'Extend the shot',
    prefill: 'Extend the shot by 4 seconds with the same scene.',
  },
];
